import React, { useState, useEffect } from 'react';
import { apiGet } from '../lib/api';
import { Building2, Users, FolderOpen, TrendingUp, ShoppingCart, Mail, Briefcase, Loader2 } from 'lucide-react';

function fmtINR(n) {
  if (!n) return '₹0';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n}`;
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div>
      <div className="kpi-overline flex items-center gap-1.5"><Icon size={11} />{label}</div>
      <div className={`text-xl font-display font-bold mt-0.5 ${accent || 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

export default function CompanyKpiStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiGet('/companies/dashboard');
        setData(d);
      } catch {
        // 403 for non-execs → hide widget entirely
        setErr(true);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 flex items-center gap-2 text-slate-400 text-xs">
        <Loader2 className="animate-spin" size={14} /> Loading company KPIs…
      </div>
    );
  }
  if (err || !data || data.length === 0) return null;

  const totals = data.reduce((acc, c) => ({
    employees: acc.employees + (c.active_employees || 0),
    projects: acc.projects + (c.active_projects || 0),
    payroll: acc.payroll + (c.monthly_payroll_inr || 0),
    pos: acc.pos + (c.open_pos || 0),
    leads: acc.leads + (c.open_leads || 0),
    opps: acc.opps + (c.open_opportunities || 0),
  }), { employees: 0, projects: 0, payroll: 0, pos: 0, leads: 0, opps: 0 });

  return (
    <div className="mb-6" data-testid="company-kpi-strip">
      <div className="flex items-center gap-2 mb-3">
        <div className="kpi-overline">Multi-Company Snapshot</div>
        <div className="flex-1 h-px bg-slate-200" />
        <div className="text-[10px] text-slate-400 uppercase tracking-wider">Executive View</div>
      </div>

      {/* Combined "Ellora Group" header (only when there are 2+ companies) */}
      {data.length > 1 && (
        <div
          className="bg-gradient-to-r from-slate-900 to-[#1c2a4d] text-white rounded-sm p-5 mb-4"
          data-testid="company-kpi-combined"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-sm bg-white/15 flex items-center justify-center">
              <Building2 size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-200 font-display font-bold">Combined · Ellora Group</div>
              <div className="font-display text-lg font-bold">All {data.length} Companies</div>
            </div>
            <div className="flex-1" />
            <div className="text-[10px] uppercase tracking-wider text-indigo-200">Board View</div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-sm">
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><Users size={11} />Total Employees</div>
              <div className="text-2xl font-display font-bold mt-1">{totals.employees}</div>
            </div>
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><FolderOpen size={11} />Active Projects</div>
              <div className="text-2xl font-display font-bold mt-1 text-emerald-300">{totals.projects}</div>
            </div>
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><TrendingUp size={11} />Monthly Payroll</div>
              <div className="text-2xl font-display font-bold mt-1 text-emerald-300">{fmtINR(totals.payroll)}</div>
            </div>
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><ShoppingCart size={11} />Open POs</div>
              <div className="text-2xl font-display font-bold mt-1 text-amber-300">{totals.pos}</div>
            </div>
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><Mail size={11} />Open Leads</div>
              <div className="text-2xl font-display font-bold mt-1 text-rose-300">{totals.leads}</div>
            </div>
            <div>
              <div className="text-[10px] text-indigo-200 uppercase tracking-wider flex items-center gap-1.5"><Briefcase size={11} />Open Opps</div>
              <div className="text-2xl font-display font-bold mt-1 text-indigo-200">{totals.opps}</div>
            </div>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 ${data.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        {data.map(c => (
          <div
            key={c.id}
            className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow"
            style={{ borderTopColor: c.primary_color || '#3b4872', borderTopWidth: 3 }}
            data-testid={`company-kpi-${c.short_key || c.id}`}
          >
            <div className="px-4 py-3 flex items-center gap-3" style={{ background: `${c.primary_color || '#3b4872'}15` }}>
              <div
                className="w-8 h-8 rounded-sm flex items-center justify-center text-white shrink-0"
                style={{ background: c.primary_color || '#3b4872' }}
              >
                <Building2 size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm text-slate-800 truncate">{c.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{c.entity_type}{c.is_parent ? ' · Parent' : ''}</div>
              </div>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3 text-xs">
              <Stat icon={Users} label="Employees" value={c.active_employees} accent="text-slate-800" />
              <Stat icon={FolderOpen} label="Projects" value={c.active_projects} accent="text-emerald-600" />
              <Stat icon={TrendingUp} label="Monthly Payroll" value={fmtINR(c.monthly_payroll_inr)} accent="text-[#3b4872]" />
              <Stat icon={ShoppingCart} label="Open POs" value={c.open_pos} accent="text-amber-700" />
              <Stat icon={Mail} label="Open Leads" value={c.open_leads} accent="text-rose-700" />
              <Stat icon={Briefcase} label="Open Opps" value={c.open_opportunities} accent="text-indigo-700" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
